import React, { useMemo } from 'react';
import { diffLines } from 'diff';
import { parseDiff } from 'react-diff-view';
import { Diff as ReactDiffViewer, Hunk } from 'react-diff-view';
import 'react-diff-view/style/index.css';
import { PreservedText } from '../preserved-text';
import { ScrollableCodeContainer } from '../claim-details/shared-timeline-styles';
import * as S from './x12-comparison-viewer.styles';

export interface X12ComparisonViewerProps {
    currentX12: string;
    previousX12?: string;
    previousTransmissionId?: string;
    showDiff: boolean;
}

export const X12ComparisonViewer: React.FC<X12ComparisonViewerProps> = ({
    currentX12,
    previousX12,
    previousTransmissionId,
    showDiff,
}) => {
    // Memoize formatted text to avoid recalculation on every render
    const { text, currentX12Text } = useMemo(() => {
        const textArray = currentX12?.split('~') || [];
        return {
            text: textArray,
            currentX12Text: textArray.join('~\n'),
        };
    }, [currentX12]);

    const previousX12Text = useMemo(() => {
        const previousTextArray = previousX12?.split('~') || [];
        return previousTextArray.join('~\n');
    }, [previousX12]);

    // Compute diff
    const diffText = useMemo(() => {
        if (!showDiff || !previousX12) return null;

        const changes = diffLines(previousX12Text, currentX12Text);

        const hunks: string[] = [];
        let oldLineCount = 0;
        let newLineCount = 0;

        changes.forEach((change) => {
            if (change.removed) {
                const lines = change.value.split('\n').filter((l) => l);
                lines.forEach((line) => {
                    hunks.push(`-${line}`);
                    oldLineCount++;
                });
            } else if (change.added) {
                const lines = change.value.split('\n').filter((l) => l);
                lines.forEach((line) => {
                    hunks.push(`+${line}`);
                    newLineCount++;
                });
            } else {
                const lines = change.value.split('\n').filter((l) => l);
                lines.forEach((line) => {
                    hunks.push(` ${line}`);
                    oldLineCount++;
                    newLineCount++;
                });
            }
        });

        const diffString = [
            '--- Previous',
            '+++ Current',
            `@@ -1,${oldLineCount} +1,${newLineCount} @@`,
            ...hunks,
        ].join('\n');

        return diffString;
    }, [showDiff, previousX12, previousX12Text, currentX12Text]);

    const { parsedDiff, parseError } = useMemo(() => {
        if (!diffText) return { parsedDiff: null, parseError: null };
        try {
            const files = parseDiff(diffText);
            return { parsedDiff: files[0], parseError: null };
        } catch (e) {
            console.error('Failed to parse X12 diff:', e);
            return { 
                parsedDiff: null, 
                parseError: `Failed to parse transmission comparison: ${e instanceof Error ? e.message : String(e)}`
            };
        }
    }, [diffText]);

    if (showDiff && parseError) {
        return (
            <S.Container>
                <S.ErrorMessage>{parseError}</S.ErrorMessage>
            </S.Container>
        );
    }

    if (showDiff && parsedDiff) {
        return (
            <S.Container>
                {previousTransmissionId && (
                    <S.TransmissionInfo>
                        Original transmission ID: {previousTransmissionId}
                    </S.TransmissionInfo>
                )}
                <S.LabelsContainer>
                    <S.DiffLabel>Original Transmission</S.DiffLabel>
                    <S.DiffLabel>Current Transmission</S.DiffLabel>
                </S.LabelsContainer>
                <ReactDiffViewer
                    viewType="split"
                    diffType={parsedDiff.type}
                    hunks={parsedDiff.hunks || []}
                >
                    {(hunks) =>
                        hunks.map((hunk, index) => <Hunk key={`hunk-${index}`} hunk={hunk} />)
                    }
                </ReactDiffViewer>
            </S.Container>
        );
    }

    if (currentX12) {
        return (
            <S.Container>
                <ScrollableCodeContainer>
                    <PreservedText text={currentX12Text} rows={text.length} />
                </ScrollableCodeContainer>
            </S.Container>
        );
    }

    return null;
};
